import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Menu, X, ArrowUpRight } from 'lucide-react'
import { useAccessibility } from '../context/AccessibilityContext'
import Links from './Link'

function Header({ setIsModalOpen }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [scrollTarget, setScrollTarget] = useState('')
  const location = useLocation()
  const navigate = useNavigate()
  const { fontSize } = useAccessibility()
  const videoRef = useRef(null)

  const toggleMenu = () => setMenuOpen((v) => !v)
  const closeMenu = () => setMenuOpen(false)

  useEffect(() => {
    if (scrollTarget) {
      const element = document.getElementById(scrollTarget)
      if (element) element.scrollIntoView({ behavior: 'smooth' })
      setScrollTarget('')
    }
  }, [scrollTarget])

  useEffect(() => {
    if (videoRef.current) videoRef.current.play().catch(() => {})
  }, [location.pathname])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const scrollToTop = (e) => {
    e?.preventDefault()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const navigateAndScroll = (target) => {
    navigate('/')
    setTimeout(() => setScrollTarget(target), 500)
  }

  const handleContactClick = (e) => {
    e.preventDefault()
    setIsModalOpen((prev) => !prev)
    closeMenu()
  }

  // Définition data-driven des liens (réutilisée desktop + mobile)
  const isHome = location.pathname === '/'
  const items = [
    isHome ? { label: 'Accueil', onClick: scrollToTop } : { label: 'Accueil', to: '/' },
    { label: 'Qui sommes-nous ?', to: '/quisommenous' },
    location.pathname === '/prestations'
      ? { label: 'Nos prestations', onClick: scrollToTop }
      : { label: 'Nos prestations', to: '/prestations' },
    isHome
      ? { label: 'Nos créations', onClick: () => setScrollTarget('Creation') }
      : { label: 'Nos créations', onClick: () => navigateAndScroll('Creation') },
  ]

  const renderItems = (className, withClose) =>
    items.map((it, i) => {
      const handle = (e) => {
        it.onClick?.(e)
        if (withClose) closeMenu()
      }
      // Vrai lien quand il y a une destination, sinon bouton (focusable au clavier)
      return it.to ? (
        <Links key={i} to={it.to} onClick={handle} className={className}>
          {it.label}
        </Links>
      ) : (
        <button key={i} type="button" onClick={handle} className={`${className} bg-transparent`}>
          {it.label}
        </button>
      )
    })

  return (
    <header className={`fixed inset-x-0 top-0 z-50 ${fontSize}`}>
      <div
        className={`transition-all duration-500 ${
          scrolled
            ? 'bg-[#0c0322]/80 backdrop-blur-xl border-b border-white/10 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.9)]'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="container-pdv flex items-center justify-between py-3">
          {/* Logo */}
          <Links
            to="/"
            onClick={scrollToTop}
            className="flex items-center shrink-0"
            aria-label="Place des Victoires — accueil"
          >
            <video
              ref={videoRef}
              width="440"
              loop
              autoPlay
              muted
              playsInline
              className="h-24 w-[4.5rem] md:h-32 md:w-24 object-cover object-center"
            >
              <source src="/logo6.webm" type="video/webm" />
            </video>
          </Links>

          {/* Nav desktop */}
          <nav aria-label="Navigation principale" className="hidden md:flex items-center gap-8">
            {renderItems(
              'links text-left text-[0.95rem] tracking-wide text-[#d7cdf2] hover:text-white transition-colors underline-animation',
              false
            )}
            <button
              onClick={handleContactClick}
              className="btn btn-primary !py-2.5 !px-5 text-[0.95rem] group"
            >
              Nous contacter
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </nav>

          {/* Hamburger mobile */}
          <button
            onClick={toggleMenu}
            className="md:hidden text-white p-2 rounded-lg glass"
            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menuOpen}
            aria-controls="menu-mobile"
          >
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Menu mobile — neutralisé (focus + lecteurs d'écran) quand fermé */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          menuOpen ? 'max-h-[26rem] opacity-100' : 'max-h-0 opacity-0'
        }`}
        inert={!menuOpen}
        aria-hidden={!menuOpen}
      >
        <nav id="menu-mobile" aria-label="Navigation principale (mobile)" className="container-pdv flex flex-col gap-1 py-4 bg-[#0c0322]/95 backdrop-blur-xl border-b border-white/10">
          {renderItems(
            'links w-full text-left py-3 px-2 text-lg text-[#d7cdf2] hover:text-white border-b border-white/5 underline-animation',
            true
          )}
          <button onClick={handleContactClick} className="btn btn-primary mt-3 w-full">
            Nous contacter
            <ArrowUpRight size={18} />
          </button>
        </nav>
      </div>
    </header>
  )
}

export default Header
